import { Box, Button, Input, useToast } from '@chakra-ui/react';
import { usePatchQaNote } from '@resistapp/client/hooks/api';
import { ChipDebugResponse, QaNote } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useChipContext } from './use-chip-context';

function getBaseId(sampleKey: string) {
  return sampleKey.replace(/-rep\d+$/, '');
}

export function QaNoteInput() {
  const { chipInfo, selectedSamples, selectedGroups, qaNotes } = useChipContext();
  const toast = useToast();
  const queryClient = useQueryClient();
  const patchNote = usePatchQaNote();
  const [text, setText] = useState('');
  const [origText, setOrigText] = useState('');

  const { sampleId, assay, enabled } = useMemo(() => {
    if (!chipInfo || !selectedSamples || !selectedGroups) return { enabled: false };
    if (selectedGroups.length !== 1 || selectedSamples.length !== 3) return { enabled: false };
    const baseIds = Array.from(new Set(selectedSamples.map(getBaseId)));
    if (baseIds.length !== 1) return { enabled: false };
    return { enabled: true, sampleId: parseInt(baseIds[0], 10), assay: selectedGroups[0] };
  }, [chipInfo, selectedSamples, selectedGroups]);

  useEffect(() => {
    if (enabled && sampleId != null && assay && chipInfo) {
      const existing = qaNotes.find(n => n.chipName === chipInfo.name && n.sampleId === sampleId && n.assay === assay);
      const noteText = existing ? existing.note : '';
      setOrigText(noteText);
      setText(noteText);
    } else {
      setOrigText('');
      setText('');
    }
  }, [enabled, sampleId, assay, qaNotes, chipInfo]);

  const handleSaveStable = useCallback(() => {
    if (!enabled || !chipInfo || sampleId == null || !assay) return;

    patchNote.mutate(
      { chipId: chipInfo.id, chipName: chipInfo.name, sampleId, assay, note: text },
      {
        onSuccess: data => {
          // Update local original text to match saved state
          setOrigText(text);

          // Update cached chip debug response so context has latest note
          const queryKey = [`/chip/${chipInfo.id}`] as const;
          queryClient.setQueryData<ChipDebugResponse | undefined>(queryKey, (old: ChipDebugResponse | undefined) => {
            if (!old) return old;
            const filtered = old.qaNotes.filter(
              (n: QaNote) => !(n.chipName === chipInfo.name && n.sampleId === sampleId && n.assay === assay),
            );
            const updatedQaNotes = data ? [...filtered, data] : filtered;
            return { ...old, qaNotes: updatedQaNotes };
          });

          toast({ title: 'QA note saved', status: 'success', duration: 3000, isClosable: true, position: 'top' });
        },
      },
    );
  }, [enabled, chipInfo, sampleId, assay, text, patchNote, queryClient, toast]);

  const placeholder = enabled ? 'Add QA note' : 'Select a single assay and sample triplicate to add a QA note';

  const isDirty = enabled && text !== origText;

  return (
    <Box mt={4} display="flex" gap={2} alignItems="center">
      <Input
        size="sm"
        placeholder={placeholder}
        value={text}
        onChange={e => {
          setText(e.target.value);
        }}
        isDisabled={!enabled}
      />
      <Button
        size="sm"
        onClick={handleSaveStable}
        isDisabled={!isDirty || patchNote.isPending || !enabled}
        isLoading={patchNote.isPending}
        colorScheme="green"
      >
        Save
      </Button>
    </Box>
  );
}
